import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { newsletterAPI, uploadAPI } from '../services/api';
import { mockTemplates, mockRiverwoodSections } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ArrowLeft,
  Download,
  Upload,
  Plus,
  Trash2,
  Eye,
  Edit3,
  Save,
  Loader2,
} from 'lucide-react';
import RiverwoodPreview from '../components/previews/RiverwoodPreview';
import Sample1Preview from '../components/previews/Sample1Preview';
import Sample2Preview from '../components/previews/Sample2Preview';
import Newsletter2022Preview from '../components/previews/Newsletter2022Preview';

const NewsletterEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { refreshLimit } = useAuth();
  const fileInputRef = useRef(null);
  
  // Check if editing existing newsletter
  const isEditing = templateId === 'edit';
  const newsletterId = isEditing ? window.location.pathname.split('/').pop() : null;
  
  const [actualTemplateId, setActualTemplateId] = useState(isEditing ? null : templateId);
  const [existingNewsletterId, setExistingNewsletterId] = useState(newsletterId);
  
  const template = mockTemplates.find((t) => t.id === actualTemplateId);
  
  // Initialize content based on template
  const [content, setContent] = useState(() => {
    if (!isEditing && templateId === 'riverwood') {
      return mockRiverwoodSections;
    }
    return isEditing ? {} : getInitialContent(templateId);
  });

  const [title, setTitle] = useState('');
  const [activeTab, setActiveTab] = useState('edit');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isUploading, setIsUploading] = useState(false);

  // Load existing newsletter if editing
  useEffect(() => {
    if (isEditing && newsletterId) {
      loadNewsletter(newsletterId);
    }
  }, [isEditing, newsletterId]);

  const loadNewsletter = async (id) => {
    try {
      const newsletter = await newsletterAPI.get(id);
      setActualTemplateId(newsletter.template_id);
      setContent(newsletter.content);
      setTitle(newsletter.title);
      setExistingNewsletterId(newsletter.newsletter_id);
    } catch (error) {
      console.error('Failed to load newsletter:', error);
      alert('Failed to load newsletter');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContentChange = (section, field, value) => {
    setContent((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleBoardMemberChange = (index, field, value) => {
    setContent((prev) => {
      const newBoard = [...prev.introductions.board];
      newBoard[index] = { ...newBoard[index], [field]: value };
      return {
        ...prev,
        introductions: {
          ...prev.introductions,
          board: newBoard,
        },
      };
    });
  };

  const handleStaffChange = (index, field, value) => {
    setContent((prev) => {
      const newStaff = [...prev.introductions.staff];
      newStaff[index] = { ...newStaff[index], [field]: value };
      return {
        ...prev,
        introductions: {
          ...prev.introductions,
          staff: newStaff,
        },
      };
    });
  };

  const handleRuleChange = (index, value) => {
    setContent((prev) => {
      const newRules = [...prev.housekeeping_rules.rules];
      newRules[index] = value;
      return {
        ...prev,
        housekeeping_rules: {
          ...prev.housekeeping_rules,
          rules: newRules,
        },
      };
    });
  };

  const addRule = () => {
    setContent((prev) => ({
      ...prev,
      housekeeping_rules: {
        ...prev.housekeeping_rules,
        rules: [...prev.housekeeping_rules.rules, ''],
      },
    }));
  };

  const removeRule = (index) => {
    setContent((prev) => ({
      ...prev,
      housekeeping_rules: {
        ...prev.housekeeping_rules,
        rules: prev.housekeeping_rules.rules.filter((_, i) => i !== index),
      },
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const result = await uploadAPI.extractContent(file);
      // Show extracted content in alert for now - user can copy/paste
      alert(`Extracted content from "${result.filename}":\n\n${result.extracted_content.substring(0, 1000)}${result.extracted_content.length > 1000 ? '...' : ''}`);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to extract content from file');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a newsletter title');
      return;
    }

    setIsSaving(true);
    try {
      if (existingNewsletterId) {
        // Update existing
        await newsletterAPI.update(existingNewsletterId, { title, content });
        alert('Newsletter saved successfully!');
      } else {
        // Create new
        const newsletter = await newsletterAPI.create(title, actualTemplateId, content);
        setExistingNewsletterId(newsletter.newsletter_id);
        await refreshLimit();
        alert('Newsletter created successfully!');
      }
    } catch (error) {
      console.error('Save failed:', error);
      const message = error.response?.data?.detail || 'Failed to save newsletter';
      alert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!existingNewsletterId) {
      // Need to save first
      if (!title.trim()) {
        alert('Please enter a newsletter title and save before downloading');
        return;
      }
      
      setIsSaving(true);
      try {
        const newsletter = await newsletterAPI.create(title, actualTemplateId, content);
        setExistingNewsletterId(newsletter.newsletter_id);
        await refreshLimit();
      } catch (error) {
        console.error('Save failed:', error);
        const message = error.response?.data?.detail || 'Failed to save newsletter';
        alert(message);
        setIsSaving(false);
        return;
      }
      setIsSaving(false);
    }

    setIsGenerating(true);
    try {
      const blob = await newsletterAPI.generatePDF(existingNewsletterId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter_${existingNewsletterId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderPreview = () => {
    switch (actualTemplateId) {
      case 'riverwood':
        return <RiverwoodPreview content={content} />;
      case 'sample1':
        return <Sample1Preview content={content} />;
      case 'sample2':
        return <Sample2Preview content={content} />;
      case 'newsletter_2022':
        return <Newsletter2022Preview content={content} />;
      default:
        return <RiverwoodPreview content={content} />;
    }
  };

  const renderEditForm = () => {
    if (actualTemplateId === 'riverwood') {
      return <RiverwoodEditForm 
        content={content} 
        onContentChange={handleContentChange}
        onBoardMemberChange={handleBoardMemberChange}
        onStaffChange={handleStaffChange}
        onRuleChange={handleRuleChange}
        onAddRule={addRule}
        onRemoveRule={removeRule}
      />;
    }
    return <GenericEditForm content={content} onContentChange={handleContentChange} templateId={actualTemplateId} />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  if (!template && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Template not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{template?.name}</h1>
              <p className="text-sm text-gray-500">{existingNewsletterId ? 'Editing newsletter' : 'New newsletter'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.doc,.docx,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Content
            </Button>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
            <Button
              onClick={handleDownloadPDF}
              disabled={isGenerating || isSaving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {isGenerating ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto p-4">
        <div className="grid lg:grid-cols-2 gap-6 h-[calc(100vh-120px)]">
          {/* Edit Panel */}
          <Card className="overflow-hidden">
            <CardHeader className="py-3 border-b bg-gray-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Edit3 className="w-4 h-4" />
                  Edit Content
                </CardTitle>
                <div className="lg:hidden flex gap-2">
                  <Button
                    variant={activeTab === 'edit' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveTab('edit')}
                  >
                    <Edit3 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant={activeTab === 'preview' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveTab('preview')}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="p-4">
                  {/* Newsletter Title */}
                  <div className="mb-6">
                    <Label className="text-sm font-medium">Newsletter Title</Label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Summer 2025 Newsletter"
                      className="mt-1"
                    />
                  </div>
                  {renderEditForm()}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Preview Panel */}
          <Card className={`overflow-hidden ${activeTab === 'edit' ? 'hidden lg:block' : ''}`}>
            <CardHeader className="py-3 border-b bg-gray-50">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 bg-gray-200">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="p-6 flex justify-center">
                  <div className="bg-white shadow-xl" style={{ width: '595px', minHeight: '842px' }}>
                    {renderPreview()}
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Riverwood Edit Form Component
const RiverwoodEditForm = ({ 
  content, 
  onContentChange, 
  onBoardMemberChange, 
  onStaffChange,
  onRuleChange,
  onAddRule,
  onRemoveRule 
}) => {
  if (!content?.header) return null;
  
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900 border-b pb-2">Header</h3>
        <div className="space-y-3">
          <div>
            <Label>Newsletter Title</Label>
            <Input
              value={content.header.title}
              onChange={(e) => onContentChange('header', 'title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.header.subtitle}
              onChange={(e) => onContentChange('header', 'subtitle', e.target.value)}
            />
          </div>
          <div>
            <Label>Season/Edition (e.g., "Spring Edition (Premier Issue)")</Label>
            <Input
              value={content.header.edition}
              onChange={(e) => onContentChange('header', 'edition', e.target.value)}
              placeholder="Spring Edition (Premier Issue)"
            />
          </div>
        </div>
      </div>

      {/* Introductions Section */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900 border-b pb-2">Introductions</h3>
        <div>
          <Label>Section Title</Label>
          <Input
            value={content.introductions.title}
            onChange={(e) => onContentChange('introductions', 'title', e.target.value)}
          />
        </div>
        <div>
          <Label>Introduction Text</Label>
          <Textarea
            value={content.introductions.body}
            onChange={(e) => onContentChange('introductions', 'body', e.target.value)}
            rows={4}
          />
        </div>

        {/* Board Members */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Board Members</Label>
          {content.introductions.board.map((member, index) => (
            <div key={index} className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Role"
                value={member.role}
                onChange={(e) => onBoardMemberChange(index, 'role', e.target.value)}
              />
              <Input
                placeholder="Name"
                value={member.name}
                onChange={(e) => onBoardMemberChange(index, 'name', e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Staff */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Staff</Label>
          {content.introductions.staff.map((staff, index) => (
            <div key={index} className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Role"
                value={staff.role}
                onChange={(e) => onStaffChange(index, 'role', e.target.value)}
              />
              <Input
                placeholder="Name"
                value={staff.name}
                onChange={(e) => onStaffChange(index, 'name', e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Management */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Management Role</Label>
            <Input
              value={content.introductions.management.role}
              onChange={(e) => onContentChange('introductions', 'management', {
                ...content.introductions.management,
                role: e.target.value,
              })}
            />
          </div>
          <div>
            <Label>Management Name</Label>
            <Input
              value={content.introductions.management.name}
              onChange={(e) => onContentChange('introductions', 'management', {
                ...content.introductions.management,
                name: e.target.value,
              })}
            />
          </div>
        </div>
      </div>

      {/* From the Board Section */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900 border-b pb-2">From the Board</h3>
        <div>
          <Label>Section Title</Label>
          <Input
            value={content.from_the_board.title}
            onChange={(e) => onContentChange('from_the_board', 'title', e.target.value)}
          />
        </div>
        <div>
          <Label>Message</Label>
          <Textarea
            value={content.from_the_board.body}
            onChange={(e) => onContentChange('from_the_board', 'body', e.target.value)}
            rows={8}
          />
        </div>
      </div>

      {/* Housekeeping Rules Section */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900 border-b pb-2">Housekeeping/House Rules</h3>
        <div>
          <Label>Section Title</Label>
          <Input
            value={content.housekeeping_rules.title}
            onChange={(e) => onContentChange('housekeeping_rules', 'title', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Rules</Label>
          {content.housekeeping_rules.rules.map((rule, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={rule}
                onChange={(e) => onRuleChange(index, e.target.value)}
                placeholder={`Rule ${index + 1}`}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemoveRule(index)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={onAddRule} className="mt-2">
            <Plus className="w-4 h-4 mr-1" />
            Add Rule
          </Button>
        </div>
      </div>
    </div>
  );
};

// Generic Edit Form for other templates
const GenericEditForm = ({ content, onContentChange, templateId }) => {
  return (
    <div className="space-y-6">
      <p className="text-gray-500 text-sm">
        Edit form for {templateId} template. Customize your content below.
      </p>
      {Object.entries(content).map(([sectionKey, sectionData]) => (
        <div key={sectionKey} className="space-y-4">
          <h3 className="font-semibold text-gray-900 border-b pb-2 capitalize">
            {sectionKey.replace(/_/g, ' ')}
          </h3>
          {typeof sectionData === 'object' && !Array.isArray(sectionData) ? (
            Object.entries(sectionData).map(([fieldKey, fieldValue]) => (
              <div key={fieldKey}>
                <Label className="capitalize">{fieldKey.replace(/_/g, ' ')}</Label>
                {typeof fieldValue === 'string' && fieldValue.length > 100 ? (
                  <Textarea
                    value={fieldValue}
                    onChange={(e) => onContentChange(sectionKey, fieldKey, e.target.value)}
                    rows={4}
                  />
                ) : typeof fieldValue === 'string' ? (
                  <Input
                    value={fieldValue}
                    onChange={(e) => onContentChange(sectionKey, fieldKey, e.target.value)}
                  />
                ) : null}
              </div>
            ))
          ) : null}
        </div>
      ))}
    </div>
  );
};

// Helper function to get initial content based on template
function getInitialContent(templateId) {
  switch (templateId) {
    case 'sample1':
      return {
        header: {
          association_name: 'Maplewood Community Association',
          newsletter_title: 'Fall/Winter Newsletter',
          year: '2025',
        },
        officers: {
          title: 'Officers',
          list: [
            { role: 'President', name: '' },
            { role: 'Vice President', name: '' },
            { role: 'Secretary', name: '' },
            { role: 'Treasurer', name: '' },
          ],
        },
        calendar: {
          title: 'Calendar',
          events: [],
        },
        contact: {
          title: 'Contact Us',
          email: '',
          address: '',
          website: '',
        },
      };
    case 'sample2':
      return {
        header: {
          company_name: 'Your HOA Name',
          tagline: 'Quarterly Update',
          title: '2025 Q3 Community Update',
        },
        summary: {
          title: 'Community Overview',
          body: '',
        },
        stats: {
          title: 'Key Statistics',
          items: [],
        },
        forecast: {
          title: 'Looking Ahead',
          body: '',
        },
        contact: {
          title: 'Contact Information',
          members: [],
        },
      };
    case 'newsletter_2022':
      return {
        header: {
          community_name: 'Vista Lakes',
          month: 'July',
          year: '2025',
          volume: '24',
          number: '7',
        },
        featured: {
          title: 'Featured Article',
          body: '',
        },
        from_board: {
          title: 'From the Board',
          body: '',
        },
        community: {
          title: 'In Our Community',
          articles: [],
        },
        calendar: {
          title: 'Calendar',
          events: [],
        },
        contacts: {
          title: 'Contacts',
          list: [],
        },
      };
    default:
      return {};
  }
}

export default NewsletterEditor;
